import React, { useState, useEffect, useRef } from 'react';
import { router, usePage } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CreditsInfo from '../../Components/CreditsInfo';
import './Create.css';

// Keep in sync with VideoController::assembleStoryPrompt.
function assemble(way, ending, styleMode, styleText) {
    const parts = [way?.prompt_text || ''];
    parts.push(ending === 'reveal'
        ? 'Tell the complete story, including the climax and how it resolves.'
        : "Instead of the climax and resolution, end by posing the screenplay's dramatic question — build toward it without revealing it.");
    if (styleMode === 'custom' && (styleText || '').trim()) {
        parts.push('For the overall_look, use this visual style as the basis and refine it into a full usable description: ' + styleText.trim());
    }
    return parts.join('\n');
}

const WAY_GLYPHS = {
    three_act: '🎬',
    save_the_cat: '🐱',
    hero_journey: '⚔️',
    character_arc: '🎭',
    protagonist_pov: '👁️',
    plot_first: '⛓️',
};

export default function Create() {
    const { movie_hash, movie_title, credits, user_email, ways, flash, errors, poster_url } = usePage().props;

    const [wayKey, setWayKey] = useState(ways[0]?.key || '');
    const [ending, setEnding] = useState('tease');
    const [styleMode, setStyleMode] = useState('ai');
    const [styleText, setStyleText] = useState('');
    const [mode, setMode] = useState('full');
    const [hqBatch, setHqBatch] = useState(false); // higher-quality batch images (NB2 1K)
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [customized, setCustomized] = useState(false);
    const [pendingRebuild, setPendingRebuild] = useState(null); // stashed settings change awaiting user choice
    const [submitting, setSubmitting] = useState(false);
    const undoStash = useRef(null);

    const way = ways.find((w) => w.key === wayKey);

    useEffect(() => {
        if (flash?.error) toast.error(flash.error);
        if (flash?.success) toast.success(flash.success);
    }, [flash]);
    useEffect(() => {
        if (errors) Object.values(errors).forEach((e) => toast.error(String(e)));
    }, [errors]);

    // Live preview while not customized; guarded when customized.
    const applySettings = (next) => {
        const merged = { wayKey, ending, styleMode, styleText, ...next };
        if (customized) {
            setPendingRebuild(merged);   // ask before clobbering edits
            return;
        }
        commit(merged);
    };

    const commit = (s) => {
        setWayKey(s.wayKey); setEnding(s.ending); setStyleMode(s.styleMode); setStyleText(s.styleText);
        const w = ways.find((x) => x.key === s.wayKey);
        setPrompt(assemble(w, s.ending, s.styleMode, s.styleText));
        setCustomized(false);
        setPendingRebuild(null);
    };

    useEffect(() => {
        // initial prompt
        setPrompt(assemble(way, ending, styleMode, styleText));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rebuild = () => {
        undoStash.current = prompt;
        commit(pendingRebuild);
        toast.info(
            <span>
                Prompt rebuilt from settings.{' '}
                <button className="btn btn-sm btn-link p-0" onClick={() => {
                    setPrompt(undoStash.current);
                    setCustomized(true);
                }}>Undo</button>
            </span>,
            { autoClose: 8000 }
        );
    };

    const keepEdits = () => {
        const s = pendingRebuild;
        // Apply the setting values (they're sent to the server regardless)
        setWayKey(s.wayKey); setEnding(s.ending); setStyleMode(s.styleMode); setStyleText(s.styleText);
        setPendingRebuild(null);
    };

    const submit = () => {
        setSubmitting(true);
        router.post(`/movies/${movie_hash}/videos/take`, {
            way_key: wayKey,
            ending,
            style_mode: styleMode,
            style_text: styleMode === 'custom' ? styleText : null,
            prompt: customized ? prompt : null,
            mode,
            image_provider: mode === 'full' && hqBatch ? 'nano_banana_2' : null,
        }, {
            onFinish: () => setSubmitting(false),
        });
    };

    return (
        <div className="container mt-4 vc-page">
            <ToastContainer position="bottom-right" />
            <CreditsInfo movie_title={movie_title} movie_hash={movie_hash} initialCredits={credits} user_email={user_email} />

            <div className="mt-3 mb-2">
                <a href={`/videos/list/${movie_hash}`} className="btn btn-sm btn-outline-secondary mb-2">← Videos</a>
            </div>

            {/* Hero: the user's movie, not a form header */}
            <div className="vc-hero">
                {poster_url && <img src={poster_url} alt="" className="vc-hero-poster" />}
                <div>
                    <h3 className="mb-1">Create your own take of <em>“{movie_title}”</em></h3>
                    <p className="text-muted mb-0">
                        Your screenplay, told as a movie — images, voice over, your angle.
                        Everything can be tweaked afterward.
                    </p>
                </div>
            </div>

            <div className="vc-panel">
                {/* THE decision — creative weight for the creative choice */}
                <div className="vc-label">How should it tell your story?</div>
                <div className="vc-ways">
                    {ways.map((w) => (
                        <button
                            key={w.key}
                            type="button"
                            className={`vc-way ${wayKey === w.key ? 'selected' : ''}`}
                            onClick={() => applySettings({ wayKey: w.key })}
                        >
                            <span className="vc-way-glyph" aria-hidden="true">{WAY_GLYPHS[w.key] || '🎞️'}</span>
                            <span className="vc-way-label">{w.label}</span>
                        </button>
                    ))}
                </div>
                {way?.description && <div className="form-text mt-2">{way.description}</div>}

                <hr className="vc-divider" />

                {/* Modifiers — side by side, quiet */}
                <div className="row g-4">
                    <div className="col-sm-6">
                        <div className="vc-label">Ending</div>
                        <div className="form-check">
                            <input className="form-check-input" type="radio" id="end-tease" checked={ending === 'tease'}
                                onChange={() => applySettings({ ending: 'tease' })} />
                            <label className="form-check-label" htmlFor="end-tease">
                                <strong>Tease it</strong>
                                <span className="vc-sub">build to the dramatic question, don't reveal it</span>
                            </label>
                        </div>
                        <div className="form-check">
                            <input className="form-check-input" type="radio" id="end-reveal" checked={ending === 'reveal'}
                                onChange={() => applySettings({ ending: 'reveal' })} />
                            <label className="form-check-label" htmlFor="end-reveal">
                                <strong>Reveal it</strong>
                                <span className="vc-sub">tell the full story, ending included</span>
                            </label>
                        </div>
                    </div>
                    <div className="col-sm-6">
                        <div className="vc-label">Visual style</div>
                        <div className="form-check">
                            <input className="form-check-input" type="radio" id="style-ai" checked={styleMode === 'ai'}
                                onChange={() => applySettings({ styleMode: 'ai' })} />
                            <label className="form-check-label" htmlFor="style-ai">
                                <strong>Let AI pick</strong>
                                <span className="vc-sub">what fits the story (recommended)</span>
                            </label>
                        </div>
                        <div className="form-check">
                            <input className="form-check-input" type="radio" id="style-custom" checked={styleMode === 'custom'}
                                onChange={() => applySettings({ styleMode: 'custom' })} />
                            <label className="form-check-label" htmlFor="style-custom"><strong>I'll describe it</strong></label>
                        </div>
                        {styleMode === 'custom' && (
                            <textarea
                                className="form-control form-control-sm mt-1"
                                rows={2}
                                value={styleText}
                                onChange={(e) => setStyleText(e.target.value)}
                                onBlur={() => applySettings({ styleText })}
                                placeholder="e.g. noir, rainy streets, high contrast"
                            />
                        )}
                    </div>
                </div>

                <hr className="vc-divider" />

                {/* Work mode */}
                <div className="vc-label">How do you want to work?</div>
                <div className="form-check">
                    <input className="form-check-input" type="radio" id="mode-full" checked={mode === 'full'}
                        onChange={() => setMode('full')} />
                    <label className="form-check-label" htmlFor="mode-full">
                        <strong>Build it all</strong>
                        <span className="vc-sub">sections, cast, images, audio — and the finished video, built automatically at the end. Typically 35–45 credits total, charged as each piece succeeds</span>
                    </label>
                </div>
                {mode === 'full' && (
                    <div className="ms-4 mb-2" role="radiogroup" aria-label="Image quality for all sections">
                        <div className="vc-sub mb-1">Image quality:</div>
                        <label className="me-3" style={{ cursor: 'pointer' }}>
                            <input type="radio" name="vcHq" className="me-1" checked={!hqBatch} onChange={() => setHqBatch(false)} />
                            Regular <span className="text-muted">(~2 cr/image)</span>
                        </label>
                        <label style={{ cursor: 'pointer' }}>
                            <input type="radio" name="vcHq" className="me-1" checked={hqBatch} onChange={() => setHqBatch(true)} />
                            Higher quality <span className="text-muted">(Nano Banana 2 · 1K, 3 cr/image)</span>
                        </label>
                    </div>
                )}
                <div className="form-check">
                    <input className="form-check-input" type="radio" id="mode-hands" checked={mode === 'hands_on'}
                        onChange={() => setMode('hands_on')} />
                    <label className="form-check-label" htmlFor="mode-hands">
                        <strong>Hands-on</strong>
                        <span className="vc-sub">sections only (5 credits) — I'll craft each image and audio myself, paying per piece as I go</span>
                    </label>
                </div>

                <hr className="vc-divider" />

                {/* Advanced — plain disclosure row, not a card */}
                <button className="vc-advanced-toggle" onClick={() => setAdvancedOpen(!advancedOpen)}>
                    {advancedOpen ? '▾' : '▸'} Advanced — edit the storytelling prompt
                    {customized && <span className="badge text-bg-warning ms-2">Customized</span>}
                </button>
                {advancedOpen && (
                    <div className="mt-2">
                        <div className="form-text mb-1">
                            This is exactly how the AI will be told to break your story into sections.
                            Edit freely or write your own — the output format is handled separately and can't break.
                        </div>
                        <textarea
                            className="form-control"
                            rows={9}
                            value={prompt}
                            onChange={(e) => { setPrompt(e.target.value); setCustomized(true); }}
                        />
                        {customized && (
                            <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => commit({ wayKey, ending, styleMode, styleText })}>
                                Reset to template
                            </button>
                        )}
                    </div>
                )}

                {/* Guarded rebuild: a setting changed while the prompt was customized */}
                {pendingRebuild && (
                    <div className="alert alert-warning mt-2 mb-0 py-2">
                        You've edited the prompt. Apply the new setting?
                        <div className="mt-1 d-flex gap-2">
                            <button className="btn btn-sm btn-warning" onClick={rebuild}>Rebuild prompt (replaces edits)</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={keepEdits}>Keep my edits</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="d-flex align-items-center gap-3 mt-3 mb-5">
                <button className="btn btn-primary btn-lg" disabled={submitting || credits < 5} onClick={submit}>
                    {submitting ? 'Creating…' : mode === 'full' ? 'Create · ~35–45 credits' : 'Create · 5 credits'}
                </button>
                {credits < 5 && <span className="text-danger small">Not enough credits — needs 5.</span>}
                <span className="text-muted small">You'll land in the editor and watch it come together.</span>
            </div>
        </div>
    );
}
